import { useMemo } from 'react';
import sortBy from 'lodash/sortBy';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { useYearTranslation } from 'approot/shared/logic/years';
import {
  IDAHO,
  ISTE,
  Subject,
  TECHNOLOGY_AND_DIGITAL_LITERACY,
} from 'global/constants';
import { useLoggingQuery } from 'lib/apollo/apollo-hooks';
import { GET_STARTUP_DATA } from './startup-data.graphql';
import type { GetStartupDataQuery } from './startup-data.graphql.generated';
import { useRegionData } from 'approot/shared/region/region.apollo';

export type Alias = {
  alias: string;
  paramName: Subject;
};

export const useGetStartupData = () => {
  const flags = useFlags();
  const { isRegionUS } = useRegionData();

  const canAccessUsDt = Boolean(flags.usDigitalTechnologies);

  const {
    data: rawData,
    loading,
    error,
  } = useLoggingQuery<GetStartupDataQuery, null>(
    GET_STARTUP_DATA,
    undefined,
    false
  );

  const { formatYears } = useYearTranslation();

  // Filter out technology-and-digital-literacy subject and IDAHO/ISTE curricula for US region when feature flag is off
  const data = useMemo(() => {
    if (!rawData?.startupData) return rawData;

    const shouldFilterUsDt = isRegionUS && !canAccessUsDt;

    if (shouldFilterUsDt) {
      const filteredDisciplines = rawData.startupData.disciplines
        ? rawData.startupData.disciplines.filter(
            discipline =>
              !discipline.curriculums.includes(IDAHO) &&
              !discipline.curriculums.includes(ISTE)
          )
        : null;

      return {
        ...rawData,
        startupData: {
          ...rawData.startupData,
          subjects: rawData.startupData.subjects.filter(
            subject => subject.name !== TECHNOLOGY_AND_DIGITAL_LITERACY
          ),
          curriculums: rawData.startupData.curriculums.filter(
            curriculum => curriculum.name !== IDAHO && curriculum.name !== ISTE
          ),
          disciplines: filteredDisciplines,
        },
      };
    }

    return rawData;
  }, [rawData, isRegionUS, canAccessUsDt]);

  const yearOptions = useMemo(
    () =>
      data?.startupData.years.map(year => ({
        label: formatYears([year.name]),
        value: year.name,
      })),
    [data?.startupData.years, formatYears]
  );

  const subjectOptions = useMemo(() => {
    return data?.startupData.subjects.map(subject => {
      return {
        value: subject.name,
        label: subject.label,
      };
    });
  }, [data?.startupData.subjects]);

  const curriculumOptions = useMemo(() => {
    return data?.startupData.curriculums.map(curriculum => {
      return {
        value: curriculum.name,
        label: curriculum.label,
      };
    });
  }, [data?.startupData.curriculums]);

  const subjectAliases = useMemo(() => {
    return data?.startupData.subjects.reduce((accum, curr) => {
      if (curr.urlNameAliases) {
        const items: Alias[] = curr.urlNameAliases.map(alias => {
          return {
            alias,
            paramName: curr.name,
          };
        });
        accum.push(...items);
      }

      return accum;
    }, [] as Alias[]);
  }, [data?.startupData]);

  const disciplines = useMemo(() => {
    const formattedDisciplines = data?.startupData.disciplines?.map(
      discipline => {
        const nameParts = discipline.name.split('-');
        return {
          ...discipline,
          name: discipline.name.includes('test')
            ? nameParts.splice(2, nameParts.length).join('-')
            : discipline.name,
        };
      }
    );
    return formattedDisciplines ? sortBy(formattedDisciplines, 'sort') : null;
  }, [data?.startupData.disciplines]);

  return {
    data,
    loading,
    error,
    yearOptions,
    subjectOptions,
    curriculumOptions,
    subjectAliases,
    disciplines,
  };
};

export const usePreloadStartupData = () => {
  useLoggingQuery<GetStartupDataQuery, null>(
    GET_STARTUP_DATA,
    undefined,
    false
  );
};
